import { ChevronNextIcon, CloseIcon, CommentIcon } from '@svgs'
import bellEmoji from '@images/emoji/bell.png'
import chatEmoji from '@images/emoji/chat.png'
import giftEmoji from '@images/emoji/gift.png'
import homeEmoji from '@images/emoji/home.png'
import priceEmoji from '@images/emoji/price.png'
import clsx from 'clsx'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/ui'
import { NotificationType } from 'src/redux/api'

export interface INotification {
  id: string
  createdAt: string
  image?: string
  icon?: string
  message: string
  text?: string
  textBold?: string
  link?: string
  linkText?: string
  chatId?: string
  type: NotificationType
  read: boolean
}

interface INotificationItem extends INotification {
  onDismiss?: (id: string, wasUnread: boolean) => void
}

// Full-colour emoji per type keep the list scannable at a glance (roadmap 41
// G1). Bundled bitmaps (src/assets/images/emoji) render identically on every
// OS — the native emoji font on Windows/Android would not match the design.
const TYPE_EMOJI: Record<string, string> = {
  NEW_MESSAGE: chatEmoji,
  NEW_PROPERTY: homeEmoji,
  PRICE_CHANGE: priceEmoji,
  SPECIAL_OFFERS: giftEmoji,
  SYSTEM: bellEmoji,
}

// Notifications created before the backend localized its system texts carry
// hardcoded English in the stored payload; map the known ones back onto i18n
// keys so old rows read in the user's language too.
const LEGACY_TEXT_KEYS: Record<string, string> = {
  'Your property has been approved!': 'property_approved',
}

// Pastel plate behind each emoji, tinted to match its artwork. The chat
// balloon is white, so it sits on a vivid green (iOS-Messages-style) instead
// of a pastel it would wash out on.
const TYPE_PLATE: Record<string, string> = {
  // eslint-disable-next-line no-restricted-syntax -- the vivid green of a chat, the same in both themes
  NEW_MESSAGE: 'bg-gradient-to-br from-green-400 to-success-strong',
  NEW_PROPERTY: 'bg-success-soft',
  PRICE_CHANGE: 'bg-warning-soft',
  SPECIAL_OFFERS: 'bg-danger-soft',
  SYSTEM: 'bg-brand-soft',
}

const NotificationItem: FC<INotificationItem> = ({
  id,
  image,
  message,
  textBold,
  text,
  link,
  linkText,
  chatId,
  type,
  read,
  onDismiss,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Notifications' })

  const emoji = TYPE_EMOJI[type] ?? TYPE_EMOJI.SYSTEM
  const plate = TYPE_PLATE[type] ?? TYPE_PLATE.SYSTEM
  // A listing photo reads better as a rounded square; a sender avatar as a circle.
  const imageShape = type === 'NEW_MESSAGE' ? 'rounded-full' : 'rounded-2xl'

  const transformedText = text ? text : t(type)
  const legacyKey = textBold ? LEGACY_TEXT_KEYS[textBold] : undefined
  const displayTextBold = legacyKey ? t(legacyKey) : textBold
  // Link already comes as complete path from backend (e.g., "/announcement/123")
  const transformedLink = link
  const transformedLinkText = linkText
    ? linkText
    : ['NEW_PROPERTY', 'PRICE_CHANGE'].includes(type)
      ? t('check_ads')
      : ''

  return (
    // an unread one is tinted with the brand; its text is the system's ink
    <div
      className={clsx(
        'flex flex-wrap gap-3 items-center w-full rounded-2xl border border-solid p-3 md:p-4',
        read ? 'bg-surface border-ink/[0.08]' : 'bg-brand-soft/60 border-brand/20',
      )}
    >
      <div className="flex flex-1 shrink gap-3.5 items-center text-ink basis-0 min-w-[12rem]">
        {image ? (
          <div className={clsx('overflow-hidden w-[3.375rem] h-[3.375rem] shrink-0', imageShape)}>
            <img src={image} alt={''} className="!object-cover size-full" />
          </div>
        ) : (
          <div
            className={clsx(
              'flex items-center justify-center rounded-2xl w-[3.375rem] h-[3.375rem] shrink-0',
              plate,
            )}
          >
            <img
              src={emoji}
              alt=""
              aria-hidden="true"
              draggable={false}
              className="w-8 h-8 object-contain"
            />
          </div>
        )}
        <div className="flex flex-col flex-1 gap-1 min-w-0 break-words">
          <div className="text-[0.9375rem] font-extrabold leading-5 text-ink">{message}</div>
          {transformedText && (
            <div className="text-sm leading-5 text-ink-2">
              {transformedText}{' '}
              {displayTextBold && <strong className="text-ink">{displayTextBold}</strong>}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 items-center">
        {chatId && (
          // an icon alone: it needs a name (the green fill carried no label)
          <Button
            link={`/messenger/${chatId}`}
            size="sm"
            variant="outlined"
            aria-label={t('open_chat')}
            className="flex-shrink-0 !px-2.5"
          >
            <span className="icon-button w-4 h-4">
              <CommentIcon />
            </span>
          </Button>
        )}
        {transformedLink && (
          <Button link={transformedLink} size="sm" className="flex-shrink-0">
            <span className="flex items-center">
              {transformedLinkText && <span className="mr-2">{transformedLinkText}</span>}
              <span className="icon-button w-2 h-2 fill-white">
                <ChevronNextIcon />
              </span>
            </span>
          </Button>
        )}
        {!read && <div className="w-2 h-2 shrink-0 bg-danger-strong rounded-full" />}
        {onDismiss && (
          <button
            type="button"
            aria-label={t('dismiss', 'Dismiss')}
            onClick={() => onDismiss(id, !read)}
            className="w-11 h-11 -m-2 flex items-center justify-center shrink-0 rounded-full fill-ink-2 hover:fill-danger"
          >
            <span className="icon-button w-3 h-3">
              <CloseIcon />
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

export default React.memo(NotificationItem)
